// /src/components/private/SideBarMenu.jsx

import { useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../src/authContext';
import { useSecurity } from '../../SecurityContext';
import Accordion from './Accordion';
import lockIcon from '../../medias/lock.svg';
import mfaIcon from '../../medias/lock-mfa.svg';
import Account from '../../pages/private/settings/Account';
import Security from '../../pages/private/settings/Security';
import Tools from '../../pages/private/settings/Tools';

export default function SidebarMenu() {
  const location = useLocation();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState('');
  const navigate = useNavigate();
  const { hasMfa } = useAuth();
  const { uiHasPermission } = useSecurity();

  // Match SecondaryNavBar permission gating: if permission exists and user lacks it → item hidden.
  const canReadSettingsAccount = uiHasPermission('private.settings.account', 'read');
  const canReadSettingsSecurity = uiHasPermission('private.settings.security', 'read');
  const canReadSettingsTools = uiHasPermission('private.settings.tools', 'read');

  const handleToggle = (section) => {
    const isMobileView = window.innerWidth < 992;
    if (!isMobileView) return;
    setExpanded(expanded === section ? '' : section);
  };

  const isActive = (path) =>
    location.pathname.includes(path) ? 'selected' : '';

  const handleSecurityClick = async () => {
    try {
      const response = await axios.post('/api/security-validate', {}, { withCredentials: true });

      if (response.data.access) {
        navigate('/settings/security');
      } else {
        navigate('/settings/security');
      }
    } catch (error) {
      console.error('Error verifying cookie:', error);
      navigate('/settings/security');
    }
  };

  return (
    <div
      className="sidebar-menu glass-card2"
      data-resource="private.settings"
      data-action="read"
      data-description="Settings menu"
    >
      <div
        className="desktop-links"
        data-resource="private.settings"
        data-action="read"
        data-description="Settings menu (desktop)"
      >
        {canReadSettingsAccount && (
          <Link
            to="/settings/account"
            className={`desktop-link ${isActive('/settings/account')}`}
            data-resource="private.settings.account"
            data-action="read"
            data-description="Settings → Account"
          >
            {t('AccountSettings')}
          </Link>
        )}

        {canReadSettingsSecurity && (
          <Link
            to="/settings/security"
            className={`desktop-link ${isActive('/settings/security')}`}
            data-resource="private.settings.security"
            data-action="read"
            data-description="Settings → Security"
            onClick={(e) => {
              e.preventDefault();
              handleSecurityClick();
            }}
          >
            {t('SecuritySettings')}
            <img
              src={hasMfa ? mfaIcon : lockIcon}
              alt={hasMfa ? 'MFA Activé' : '🔒'}
              className="lock-icon"
            />
          </Link>
        )}

        {canReadSettingsTools && (
          <Link
            to="/settings/tools"
            className={`desktop-link ${isActive('/settings/tools')}`}
            data-resource="private.settings.tools"
            data-action="read"
            data-description="Settings → Tools"
          >
            {t('toolsSettings')}
          </Link>
        )}
      </div>

      <div
        className="mobile-links"
        data-resource="private.settings"
        data-action="read"
        data-description="Settings menu (mobile)"
      >
        {canReadSettingsAccount && (
          <div
            data-resource="private.settings.account"
            data-action="read"
            data-description="Settings → Account (mobile)"
          >
            <Accordion
              title={t('AccountSettings')}
              isOpen={expanded === 'account'}
              toggle={() => handleToggle('account')}
            >
              <Account />
            </Accordion>
          </div>
        )}

        {canReadSettingsSecurity && (
          <div
            data-resource="private.settings.security"
            data-action="read"
            data-description="Settings → Security (mobile)"
          >
            <Accordion
              title={t('SecuritySettings')}
              icon={
                <img
                  src={hasMfa ? mfaIcon : lockIcon}
                  alt={hasMfa ? 'MFA Activé' : '🔒'}
                  className="lock-icon"
                />
              }
              isOpen={expanded === 'security'}
              toggle={() => handleToggle('security')}
            >
              <Security />
            </Accordion>
          </div>
        )}

        {canReadSettingsTools && (
          <div
            data-resource="private.settings.tools"
            data-action="read"
            data-description="Settings → Tools (mobile)"
          >
            <Accordion
              title={t('toolsSettings')}
              isOpen={expanded === 'tools'}
              toggle={() => handleToggle('tools')}
            >
              <Tools />
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
}
